import { observable, action, makeObservable, toJS } from 'mobx';

import { BridgeWidgetId, GRID_COLUMNS, WidgetLayout } from '../models/bridge.model';
import storageService from '../services/storage.service';

const STORAGE_KEY = 'bridgeLayout';
const LAYOUT_VERSION = 3;

export const defaultLayout: WidgetLayout[] = [
  { id: BridgeWidgetId.HealthRates, visible: true, span: GRID_COLUMNS },
  // hidden by default — health rates already shows these percentages
  { id: BridgeWidgetId.Gauges, visible: false, span: 3 },
  { id: BridgeWidgetId.Loglytics, visible: true, span: GRID_COLUMNS },
  { id: BridgeWidgetId.DeviceChart, visible: true, span: GRID_COLUMNS },
  { id: BridgeWidgetId.AffectedChart, visible: true, span: GRID_COLUMNS },
  { id: BridgeWidgetId.LogsChart, visible: true, span: GRID_COLUMNS },
];

export class BridgeLayoutStore {
  layout: WidgetLayout[];
  editing = false;

  constructor() {
    this.layout = this.load();
    makeObservable(this, {
      layout: observable,
      editing: observable,
      setEditing: action,
      toggleVisible: action,
      setSpan: action,
      move: action,
      reset: action
    });
  }

  setEditing(editing: boolean) {
    this.editing = editing;
  }

  toggleVisible(id: BridgeWidgetId) {
    const widget = this.layout.find(w => w.id === id)!;
    widget.visible = !widget.visible;
    this.save();
  }

  setSpan(id: BridgeWidgetId, span: number) {
    const widget = this.layout.find(w => w.id === id)!;
    widget.span = Math.min(GRID_COLUMNS, Math.max(1, span));
    this.save();
  }

  move(activeId: string, overId: string) {
    const from = this.layout.findIndex(w => w.id === activeId);
    const to = this.layout.findIndex(w => w.id === overId);
    if (from < 0 || to < 0) return;
    const [moved] = this.layout.splice(from, 1);
    this.layout.splice(to, 0, moved);
    this.save();
  }

  reset() {
    this.layout = defaultLayout.map(w => ({...w}));
    this.save();
  }

  private load(): WidgetLayout[] {
    const defaults = defaultLayout.map(w => ({...w}));
    const raw = storageService.getItem(STORAGE_KEY, '');
    if (!raw) return defaults;
    try {
      const stored = JSON.parse(raw) as { version: number; widgets: WidgetLayout[] };
      if (stored.version !== LAYOUT_VERSION) return defaults;
      // unknown ids are dropped; widgets added after the layout was saved keep their default spot
      const merged = stored.widgets.filter(w => defaults.some(d => d.id === w.id));
      defaults.forEach((d, index) => {
        if (!merged.some(w => w.id === d.id)) merged.splice(index, 0, {...d});
      });
      return merged;
    } catch {
      return defaults;
    }
  }

  private save() {
    storageService.setItem(STORAGE_KEY, JSON.stringify({ version: LAYOUT_VERSION, widgets: toJS(this.layout) }));
  }
}

export default new BridgeLayoutStore();
